/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { isEmpty } from '@ember/utils';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import espInfo, { EspKey, ESP_AUTHENTICATION_OPTIONS } from 'cio/utils/email-service-providers';
import MailServerValidations from 'cio/utils/validations/mail-server';
import FormValidations from 'cio/utils/form-validations';

import MailServerModel from 'cio/models/mail-server';
import AuthService from 'cio/services/auth';

interface CreateServerModalSetupCredentialsStepArgs {
  selectedEsp: EspKey;
  server: MailServerModel;
  onClose: () => unknown;
  onGoBack: () => unknown;
  onSave: () => unknown;
}

export default class CreateServerModalSetupCredentialsStep extends Component<CreateServerModalSetupCredentialsStepArgs> {
  @service auth!: AuthService;

  @tracked hasChanged = false;
  @tracked error?: string;
  @tracked hasFailed = false;

  EspKey = EspKey;

  readonly validations = FormValidations.create({
    validations: MailServerValidations,
    context: this,
    modelKey: 'args.server',
  });

  readonly authenticationOptions = ESP_AUTHENTICATION_OPTIONS;

  get selectedEspInfo() {
    return espInfo(this.args.selectedEsp);
  }

  get hasMultipleAddresses() {
    let info = this.selectedEspInfo;
    return typeof info.address === 'object' && info.address?.default != null;
  }

  get addresses() {
    if (!this.hasMultipleAddresses) {
      return [];
    }

    return Object.values(this.selectedEspInfo.address ?? {}).map((a) => ({ label: a, value: a }));
  }

  @computed(
    'auth.user.permissions',
    'account.current.readOnly',
    'masq.{isOn,readOnly}',
    'saveTask.isRunning',
    'validations.hasErrors',
    'hasChanged',
  )
  get saveIsDisabled() {
    return (
      !this.auth.hasPermission('journeys/email_message_settings/create') ||
      this.saveTask.isRunning ||
      this.validations.hasErrors ||
      !this.hasChanged
    );
  }

  saveTask = task(async () => {
    try {
      await this.validations.validateModel();
      await this.args.onSave();
    } catch (reason) {
      if (reason.errors?.length > 0) {
        this.error = reason.errors[0].detail;
      }

      this.hasFailed = true;
    }
  });

  @action
  onChange(attribute: keyof MailServerModel, value: string) {
    this.hasChanged = true;
    this.error = undefined;
    this.hasFailed = false;

    this.args.server.set(attribute, value);

    if (!isEmpty((MailServerValidations as any)[attribute])) {
      this.validations.validateAttribute(attribute);
    }
  }
}
